<template>
  <div class="u-date-picker w-full">
    <Popover v-slot="{ close }" class="relative block">
      <PopoverButton
        :disabled="disabled"
        :class="[
          'u-date-picker-trigger flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-start text-sm transition-colors',
          'bg-[var(--theme-surface)] border-[var(--theme-border)] text-[var(--theme-text)]',
          'placeholder-[var(--theme-text-tertiary)]',
          'hover:border-[var(--theme-text-tertiary)]/40 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary-alpha)] focus:border-[var(--theme-primary)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          inputClass,
          error ? 'u-date-picker-trigger--error border-[var(--theme-error)] ring-2 ring-[var(--theme-error)]/20' : ''
        ]"
      >
        <span class="truncate">{{ displayText }}</span>
        <span class="shrink-0 text-[var(--theme-text-tertiary)]" aria-hidden="true">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </span>
      </PopoverButton>
      <PopoverPanel
        class="u-date-picker-panel absolute start-0 z-50 mt-1 w-auto rounded-lg border border-[var(--theme-border)] bg-[var(--theme-card)] p-2 shadow-lg"
      >
        <UCalendar
          v-model="calendarValue"
          class="u-date-picker-calendar p-2"
          :prevent-deselect="true"
          @update:model-value="(val) => onCalendarSelect(val, close)"
        />
      </PopoverPanel>
    </Popover>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { CalendarDate, getLocalTimeZone } from '@internationalized/date';
import UCalendar from '@nuxt/ui/components/Calendar.vue';

function parseYYYYMMDD(str) {
  if (!str || typeof str !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return new CalendarDate(y, m, d);
}

function toYYYYMMDD(date) {
  if (!date) return '';
  const y = date.year;
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');
  return `${y}-${month}-${day}`;
}

export default {
  name: 'UDatePicker',
  components: { Popover, PopoverButton, PopoverPanel, UCalendar },
  props: {
    modelValue: { type: [String, null], default: '' },
    placeholder: { type: String, default: '' },
    color: { type: String, default: 'neutral' },
    variant: { type: String, default: 'outline' },
    size: { type: String, default: 'md' },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    inputClass: { type: [String, Object, Array], default: '' },
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const calendarValue = ref(parseYYYYMMDD(props.modelValue) ?? new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate()));

    watch(() => props.modelValue, (val) => {
      const parsed = parseYYYYMMDD(val);
      if (parsed) calendarValue.value = parsed;
      else if (!val) calendarValue.value = new CalendarDate(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());
    }, { immediate: true });

    const displayText = computed(() => {
      if (props.modelValue) {
        try {
          const d = parseYYYYMMDD(props.modelValue);
          if (d) return d.toDate(getLocalTimeZone()).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        } catch (_) {}
        return props.modelValue;
      }
      return props.placeholder || 'Select date';
    });

    function onCalendarSelect(val, close) {
      const str = toYYYYMMDD(val);
      emit('update:modelValue', str);
      emit('change', str);
      if (typeof close === 'function') close();
    }

    return { calendarValue, displayText, onCalendarSelect };
  },
};
</script>

<style scoped>
/* Theme-aware calendar panel: read from site CSS variables */
.u-date-picker-panel {
  color: var(--theme-text);
}
.u-date-picker-panel [data-slot="heading"] {
  color: var(--theme-text);
}
.u-date-picker-panel [data-slot="headCell"] {
  color: var(--theme-text-tertiary);
}
.u-date-picker-panel [data-slot="cellTrigger"] {
  color: var(--theme-text);
}
.u-date-picker-panel [data-slot="cellTrigger"]:hover {
  background-color: color-mix(in srgb, var(--theme-primary) 15%, transparent);
}
.u-date-picker-panel [data-slot="cellTrigger"][data-selected] {
  background-color: var(--theme-primary);
  color: var(--theme-text-on-primary);
}
.u-date-picker-panel [data-slot="cellTrigger"][data-today]:not([data-selected]) {
  color: var(--theme-primary);
  font-weight: 600;
}
.u-date-picker-panel [data-slot="cellTrigger"][data-highlighted] {
  background-color: color-mix(in srgb, var(--theme-primary) 20%, transparent);
}
.u-date-picker-panel [data-slot="cellTrigger"][data-outside-view] {
  color: var(--theme-text-tertiary);
  opacity: 0.6;
}
/* Nav buttons (prev/next month) inside calendar */
.u-date-picker-panel button[data-slot]:not([data-slot="cellTrigger"]) {
  color: var(--theme-text-secondary);
}
.u-date-picker-panel button[data-slot]:not([data-slot="cellTrigger"]):hover {
  color: var(--theme-text);
  background-color: var(--theme-surface);
}
.u-date-picker-panel [data-slot="cellWeek"] {
  color: var(--theme-text-tertiary);
}
</style>

<!-- Admin panel: white/slate to match admin form fields (no scoped so .admin-panel ancestor works) -->
<style>
.admin-panel .u-date-picker .u-date-picker-trigger {
  background-color: #ffffff;
  border-color: #e2e8f0;
  color: #1e293b;
}
.admin-panel .u-date-picker .u-date-picker-trigger:hover {
  border-color: #cbd5e1;
}
.admin-panel .u-date-picker .u-date-picker-trigger:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.admin-panel .u-date-picker .u-date-picker-trigger .shrink-0 {
  color: #94a3b8;
}
.admin-panel .u-date-picker .u-date-picker-trigger.u-date-picker-trigger--error {
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

.admin-panel .u-date-picker .u-date-picker-panel {
  background-color: #ffffff;
  border-color: #e2e8f0;
  color: #334155;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="heading"] {
  color: #1e293b;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="headCell"] {
  color: #64748b;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="cellTrigger"] {
  color: #334155;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="cellTrigger"]:hover {
  background-color: #f1f5f9;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="cellTrigger"][data-selected] {
  background-color: #3b82f6;
  color: #ffffff;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="cellTrigger"][data-today]:not([data-selected]) {
  color: #3b82f6;
  font-weight: 600;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="cellTrigger"][data-highlighted] {
  background-color: #dbeafe;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="cellTrigger"][data-outside-view] {
  color: #94a3b8;
  opacity: 0.6;
}
.admin-panel .u-date-picker .u-date-picker-panel button[data-slot]:not([data-slot="cellTrigger"]) {
  color: #64748b;
}
.admin-panel .u-date-picker .u-date-picker-panel button[data-slot]:not([data-slot="cellTrigger"]):hover {
  color: #334155;
  background-color: #f1f5f9;
}
.admin-panel .u-date-picker .u-date-picker-panel [data-slot="cellWeek"] {
  color: #94a3b8;
}
</style>
